import React from "react";
import { Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface InsuranceHeroProps {
  providerName: string;
  shortName: string;
  memberTerm: string;
}

/**
 * Insurance page hero — building photo background with left-side navy
 * overlay so text reads clearly while the photo stays visible on the right.
 * Matches the home page hero format.
 */
export const InsuranceHero: React.FC<InsuranceHeroProps> = ({
  providerName,
  shortName,
  memberTerm,
}) => (
  <section className="relative text-primary-foreground py-14 sm:py-24 overflow-hidden bg-primary">
    {/* Clean navy gradient background */}
    <div className="absolute inset-0 bg-gradient-to-br from-primary/95 via-primary/90 to-primary-deep/90" />
    <div className="container-px relative mx-auto max-w-5xl px-4">
      <div className="max-w-xl text-left space-y-5">
        <span className="inline-block rounded-full bg-accent/90 backdrop-blur-sm px-5 py-2 text-xs sm:text-sm font-bold text-white shadow-lg uppercase tracking-wider">
          Insurance Partner & Coverage
        </span>
        <h1 className="font-headings text-2xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight drop-shadow-md">
          TMS & Mental Health Treatment for {providerName}{" "}
          {memberTerm.charAt(0).toUpperCase() + memberTerm.slice(1)}
        </h1>
        <p className="text-base sm:text-lg text-primary-foreground/90 leading-relaxed drop-shadow-md">
          Have {shortName} insurance and are looking for treatment options for
          depression or other mental-health concerns? Neuro Wellness TMS Centers
          of America can help you understand your options and determine whether
          our services may be available through your specific plan.
        </p>
        <div className="flex flex-col sm:flex-row items-start justify-start gap-4 pt-3">
          <Button
            asChild
            size="lg"
            className="w-full sm:w-auto bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90 text-base px-8 py-6"
          >
            <a href="#verify-form">Check My Insurance Options</a>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="w-full sm:w-auto border-2 border-white/60 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 hover:border-white/80 text-base px-8 py-6"
          >
            <a href="tel:9548279793">
              <Phone className="h-5 w-5 mr-2" />
              Call 954-827-9793
            </a>
          </Button>
        </div>
      </div>
    </div>
  </section>
);

export default InsuranceHero;
